import { useState, useRef, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { COUNTRIES, findCountryByCode, parseE164, applyMask, cursorToDigitIndex, digitIndexToCursor } from './countries.ts'
import type { Country } from './countries.ts'
import styles from './PhoneInput.module.css'

export interface PhoneInputProps {
  value?: string
  onChange?: (value: string) => void
  defaultCountry?: string
  error?: boolean
  placeholder?: string
  disabled?: boolean
  id?: string
  className?: string
}

function useDropdownPosition(
  anchorRef: React.RefObject<HTMLButtonElement | null>,
  open: boolean,
) {
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0 })

  useEffect(() => {
    if (!open || !anchorRef.current) return

    function update() {
      /* v8 ignore next -- ref is always attached while the component is mounted */
      if (!anchorRef.current) return
      const rect = anchorRef.current.getBoundingClientRect()
      setPos({
        top: rect.bottom + 4,
        left: rect.left,
        width: Math.max(280, rect.width),
      })
    }

    update()
    window.addEventListener('scroll', update, true)
    window.addEventListener('resize', update)
    return () => {
      window.removeEventListener('scroll', update, true)
      window.removeEventListener('resize', update)
    }
  }, [anchorRef, open])

  return pos
}

export function PhoneInput({
  value,
  onChange,
  defaultCountry = 'US',
  error,
  placeholder,
  disabled,
  id,
  className,
}: PhoneInputProps) {
  const [selectedCountry, setSelectedCountry] = useState<Country>(
    () => findCountryByCode(defaultCountry) ?? COUNTRIES[0],
  )
  const [digits, setDigits] = useState('')
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const wrapperRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const formatted = applyMask(digits, selectedCountry.mask)
  // The mask filled with zeros. Only its # slots take digits — feeding the
  // mask's own brackets and dashes back in doubled them: "((00) 0) -000-0000".
  const placeholderText = placeholder ?? selectedCountry.mask.replace(/#/g, '0')
  const dropdownPos = useDropdownPosition(buttonRef, open)

  // Sync from controlled value
  useEffect(() => {
    if (value === undefined) return
    const parsed = parseE164(value)
    if (parsed) {
      setSelectedCountry(parsed.country)
      setDigits(parsed.localNumber)
    } else if (value === '') {
      setDigits('')
    }
  }, [value])

  // Close dropdown on outside click (check both wrapper and portal dropdown)
  useEffect(() => {
    if (!open) return
    function handleClick(e: MouseEvent) {
      const target = e.target as Node
      const inWrapper = wrapperRef.current?.contains(target)
      const inDropdown = dropdownRef.current?.contains(target)
      if (!inWrapper && !inDropdown) {
        setOpen(false)
        setSearch('')
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [open])

  // Focus search when dropdown opens
  useEffect(() => {
    if (open && searchRef.current) {
      searchRef.current.focus()
    }
  }, [open])

  const fireChange = useCallback(
    (country: Country, rawDigits: string) => {
      onChange?.(rawDigits ? `${country.dial}${rawDigits}` : '')
    },
    [onChange],
  )

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const input = inputRef.current
    /* v8 ignore next -- unreachable defensive guard */
    if (!input) return

    const rawValue = e.target.value
    const cursorPos = input.selectionStart ?? rawValue.length

    // Figure out how many digits exist up to the cursor in the new raw value
    const newDigitIndex = cursorToDigitIndex(rawValue, cursorPos)

    // Extract only digits from the new value
    const newDigits = rawValue.replace(/\D/g, '')
    setDigits(newDigits)
    fireChange(selectedCountry, newDigits)

    // Restore cursor position after React re-renders
    requestAnimationFrame(() => {
      if (!inputRef.current) return
      const newFormatted = applyMask(newDigits, selectedCountry.mask)
      const newCursor = digitIndexToCursor(newFormatted, newDigitIndex)
      inputRef.current.setSelectionRange(newCursor, newCursor)
    })
  }

  function handleCountrySelect(country: Country) {
    setSelectedCountry(country)
    setOpen(false)
    setSearch('')
    fireChange(country, digits)
    // Focus the number input after selecting
    requestAnimationFrame(() => inputRef.current?.focus())
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Escape') {
      setOpen(false)
      setSearch('')
    }
  }

  const filtered = search
    ? COUNTRIES.filter(
        (c) =>
          c.name.toLowerCase().includes(search.toLowerCase()) ||
          c.dial.includes(search) ||
          c.code.toLowerCase().includes(search.toLowerCase()),
      )
    : COUNTRIES

  const wrapperClasses = [
    styles.wrapper,
    error && styles.error,
    disabled && styles.disabled,
    className,
  ]
    .filter(Boolean)
    .join(' ')

  const dropdown = open
    ? createPortal(
        <div
          ref={dropdownRef}
          className={styles.dropdown}
          role="listbox"
          style={{
            position: 'fixed',
            top: dropdownPos.top,
            left: dropdownPos.left,
            width: dropdownPos.width,
          }}
          onKeyDown={handleKeyDown}
        >
          <input
            ref={searchRef}
            type="text"
            className={styles.dropdownSearch}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search countries..."
            autoComplete="off"
          />
          <div className={styles.dropdownList}>
            {filtered.map((country) => (
              <button
                key={country.code}
                type="button"
                role="option"
                aria-selected={country.code === selectedCountry.code}
                className={[
                  styles.countryOption,
                  country.code === selectedCountry.code && styles.countryOptionSelected,
                ]
                  .filter(Boolean)
                  .join(' ')}
                onClick={() => handleCountrySelect(country)}
              >
                <span className={styles.optionFlag}>{country.flag}</span>
                <span className={styles.optionName}>{country.name}</span>
                <span className={styles.optionDial}>{country.dial}</span>
              </button>
            ))}
            {filtered.length === 0 && (
              <div className={styles.noResults}>No countries found</div>
            )}
          </div>
        </div>,
        document.body,
      )
    : null

  return (
    <div ref={wrapperRef} className={wrapperClasses} onKeyDown={handleKeyDown}>
      <button
        ref={buttonRef}
        type="button"
        className={styles.countryButton}
        onClick={() => !disabled && setOpen(!open)}
        disabled={disabled}
        aria-label={`Select country. Current: ${selectedCountry.name}`}
        aria-expanded={open}
      >
        <span className={styles.flag}>{selectedCountry.flag}</span>
        <span className={styles.dial}>{selectedCountry.dial}</span>
        <span className={styles.chevron} aria-hidden="true">&#9662;</span>
      </button>

      <input
        ref={inputRef}
        id={id}
        type="tel"
        className={styles.numberInput}
        value={formatted}
        onChange={handleInputChange}
        placeholder={placeholderText}
        disabled={disabled}
        autoComplete="tel-national"
      />

      {dropdown}
    </div>
  )
}
